import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Heart, Brain, Star, Shield, Sun, Church, Gift, Lightbulb, Coins, Home, Users } from 'lucide-react';

interface Theme {
  title: string;
  description: string;
  verses: string[];
  icon: React.ComponentType<any>;
}

interface ThemeCardProps {
  theme: Theme;
}

export function BiblicalThemes() {
  const themes = [
    // Core Spiritual Themes
    {
      title: 'Love and Relationships',
      description: 'Biblical guidance on love, marriage, and relationships.',
      verses: ['1 Corinthians 13:4-7', 'Ephesians 5:25-33', 'Song of Solomon 8:6-7', 'Genesis 2:24'],
      icon: Heart
    },
    {
      title: 'Faith and Trust',
      description: 'Building and maintaining faith in challenging times.',
      verses: ['Hebrews 11:1', 'Romans 10:17', 'Mark 11:22-24', 'Proverbs 3:5-6'],
      icon: Star
    },
    {
      title: 'Heaven and Afterlife',
      description: 'Biblical teachings about heaven, eternal life, and resurrection.',
      verses: ['John 14:2-3', 'Revelation 21:4', '1 Corinthians 15:42-44', 'Philippians 3:20-21'],
      icon: Sun
    },
    {
      title: 'Peace and Comfort',
      description: 'Finding peace and comfort in God during difficult times.',
      verses: ['Philippians 4:6-7', 'John 14:27', 'Psalm 23:4', 'Isaiah 41:10'],
      icon: Shield
    },
    {
      title: 'Purpose and Calling',
      description: 'Understanding God\'s purpose and calling for your life.',
      verses: ['Jeremiah 29:11', 'Romans 8:28', 'Ephesians 2:10', 'Philippians 1:6'],
      icon: Star
    },
    // Personal Growth
    {
      title: 'Wisdom and Knowledge',
      description: 'Growing in wisdom and understanding God\'s truth.',
      verses: ['Proverbs 1:7', 'James 1:5', 'Colossians 2:2-3', 'Psalm 119:105'],
      icon: Brain
    },
    {
      title: 'Strength and Courage',
      description: 'Finding strength and courage through faith.',
      verses: ['Joshua 1:9', 'Isaiah 40:31', 'Philippians 4:13', '2 Timothy 1:7'],
      icon: Shield
    },
    // Life Challenges
    {
      title: 'Overcoming Fear',
      description: 'Biblical guidance for dealing with fear and anxiety.',
      verses: ['Psalm 34:4', '2 Timothy 1:7', 'Isaiah 41:10', '1 Peter 5:7'],
      icon: Shield
    },
    {
      title: 'Healing and Health',
      description: 'Scriptures about physical and spiritual healing.',
      verses: ['James 5:14-15', 'Psalm 103:2-3', 'Isaiah 53:5', '3 John 1:2'],
      icon: Heart
    },
    // Spiritual Growth
    {
      title: 'Prayer and Worship',
      description: 'Deepening your prayer life and worship.',
      verses: ['Philippians 4:6', 'James 5:16', 'Psalm 95:1-2', 'Matthew 6:9-13'],
      icon: Church
    },
    {
      title: 'Grace and Forgiveness',
      description: 'Understanding God\'s grace and learning to forgive.',
      verses: ['Ephesians 2:8-9', 'Colossians 3:13', '1 John 1:9', 'Matthew 6:14-15'],
      icon: Gift
    },
    // Life Direction
    {
      title: 'Decision Making',
      description: 'Biblical principles for making wise decisions.',
      verses: ['James 1:5-6', 'Proverbs 3:5-6', 'Psalm 32:8', 'Romans 12:2'],
      icon: Lightbulb
    },
    {
      title: 'Financial Wisdom',
      description: 'Biblical principles for managing finances.',
      verses: ['Proverbs 3:9-10', 'Malachi 3:10', 'Matthew 6:24', '1 Timothy 6:10'],
      icon: Coins
    },
    // Community and Relationships
    {
      title: 'Family Values',
      description: 'Biblical teachings on family relationships.',
      verses: ['Ephesians 6:1-4', 'Colossians 3:20-21', 'Proverbs 22:6', 'Psalm 127:3'],
      icon: Home
    },
    {
      title: 'Leadership',
      description: 'Biblical principles of servant leadership.',
      verses: ['1 Peter 5:2-3', 'Titus 1:7-9', 'Proverbs 16:12', 'Mark 10:42-45'],
      icon: Users
    }
  ];

  return (
    <div className="space-y-6">
      {/* Categories Grid */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {themes.map((theme, index) => (
          <ThemeCard key={index} theme={theme} />
        ))}
      </div>
    </div>
  );
}

function ThemeCard({ theme }: ThemeCardProps) {
  const navigate = useNavigate();

  const handleVerseClick = (verseRef: string) => {
    // Parse verse reference
    const [bookChapter, verse] = verseRef.split(':');
    const [book, chapter] = bookChapter.split(' ').reduce((acc: string[], part, index, arr) => {
      if (index === arr.length - 1) {
        acc[1] = part; // chapter
      } else {
        acc[0] = (acc[0] ? acc[0] + ' ' : '') + part; // book name
      }
      return acc;
    }, ['', '']);

    // Navigate to the Bible reader with the correct book and chapter
    navigate(`/read/kjv/${book}/${chapter}`);
  };

  const Icon = theme.icon;

  return (
    <div className="bg-obsidian-800 border border-gold-500/20 rounded-xl overflow-hidden hover:border-gold-500/40 transition-colors">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-gold-500" />
          </div>
          <h3 className="text-xl font-semibold text-gold-200">{theme.title}</h3>
        </div>

        {/* Description */}
        <p className="text-white/60 mb-4 text-sm">{theme.description}</p>

        {/* Verses */}
        <div className="space-y-2">
          {theme.verses.map((verse, index) => (
            <button
              key={index}
              onClick={() => handleVerseClick(verse)}
              className="w-full text-left px-3 py-2 rounded-lg text-sm text-gold-200/80 hover:text-gold-200 hover:bg-gold-500/10 transition-colors"
            >
              {verse}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
